import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { QrPanel } from "../../components/QrPanel/QrPanel";
import { MemberTable } from "../../components/MemberTable/MemberTable";
import { InlineNotice } from "../../components/InlineNotice/InlineNotice";
import { Button } from "../../components/Button/Button";
import { IconArrowRight } from "../../components/icons/icons";
import { BrandSettings } from "./BrandSettings";
import { AnalyticsPanel, type AnalyticsState } from "./AnalyticsPanel";
import { ApiError } from "../../lib/apiClient";
import type { Member, MemberAnalytics, MemberPage, Tenant } from "../../types";
import styles from "./TenantWorkspace.module.css";

type FetchMembers = (
  token: string,
  tenantId: string,
  params?: { limit?: number; cursor?: string },
) => Promise<MemberPage>;

type FetchAnalytics = (token: string, tenantId: string) => Promise<{ analytics: MemberAnalytics }>;

const ACCESS_DENIED_MESSAGE =
  "Access denied for this business. If your access changed recently, sign out and sign in again to refresh it.";

type WorkspaceTab = "overview" | "analytics" | "customize" | "members";

const WORKSPACE_TABS: ReadonlyArray<{ key: WorkspaceTab; label: string }> = [
  { key: "overview", label: "Overview" },
  { key: "analytics", label: "Analytics" },
  { key: "customize", label: "Customize" },
  { key: "members", label: "Members" },
];

interface TenantWorkspaceProps {
  tenant: Tenant;
  token: string;
  fetchMembers: FetchMembers;
  fetchAnalytics: FetchAnalytics;
  onUnauthorized: () => void;
  /** True for the assigned owner; false for administrators, who see the brand kit read-only. */
  canEditBrand: boolean;
  /** Called with the fresh Tenant after a successful brand-kit save. */
  onTenantUpdated?: (tenant: Tenant) => void;
  /** Admin-only context (e.g. assigned owner emails, invitation status) rendered at the top of the sheet. */
  headerNotice?: ReactNode;
}

export function TenantWorkspace({
  tenant,
  token,
  fetchMembers,
  fetchAnalytics,
  onUnauthorized,
  canEditBrand,
  onTenantUpdated,
  headerNotice,
}: TenantWorkspaceProps) {
  const [members, setMembers] = useState<Member[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);
  const [loadingFirstPage, setLoadingFirstPage] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<WorkspaceTab>("overview");
  const [analyticsState, setAnalyticsState] = useState<AnalyticsState>({ status: "loading" });
  const [analyticsRetry, setAnalyticsRetry] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const idBase = `tenant-${tenant.tenantId.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

  useEffect(() => {
    let cancelled = false;
    setMembers([]);
    setCursor(undefined);
    setHasMore(false);
    setError(null);
    setLoadingFirstPage(true);

    fetchMembers(token, tenant.tenantId)
      .then((page) => {
        if (cancelled) return;
        setMembers(page.members);
        setCursor(page.nextCursor);
        setHasMore(Boolean(page.nextCursor));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorized();
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setError(ACCESS_DENIED_MESSAGE);
          return;
        }
        setError(err instanceof Error ? err.message : "Couldn't load members. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoadingFirstPage(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.tenantId, token, fetchMembers]);

  useEffect(() => {
    setAnalyticsState({ status: "loading" });
  }, [tenant.tenantId, token]);

  useEffect(() => {
    if (activeTab !== "analytics") return;
    let cancelled = false;
    setAnalyticsState({ status: "loading" });
    fetchAnalytics(token, tenant.tenantId)
      .then(({ analytics }) => {
        if (!cancelled) setAnalyticsState({ status: "ready", analytics });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthorized();
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setAnalyticsState({ status: "error", message: ACCESS_DENIED_MESSAGE });
          return;
        }
        setAnalyticsState({
          status: "error",
          message: err instanceof Error ? err.message : "Couldn't load analytics. Please try again.",
        });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, tenant.tenantId, token, fetchAnalytics, analyticsRetry]);

  async function handleLoadMore() {
    setLoadingMore(true);
    setError(null);
    try {
      const page = await fetchMembers(token, tenant.tenantId, { cursor });
      setMembers((prev) => [...prev, ...page.members]);
      setCursor(page.nextCursor);
      setHasMore(Boolean(page.nextCursor));
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onUnauthorized();
        return;
      }
      if (err instanceof ApiError && err.status === 403) {
        setError(ACCESS_DENIED_MESSAGE);
        return;
      }
      setError(err instanceof Error ? err.message : "Couldn't load more members. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  }

  function goToTab(tab: WorkspaceTab) {
    setActiveTab(tab);
    tabRefs.current[WORKSPACE_TABS.findIndex((candidate) => candidate.key === tab)]?.focus();
  }

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    let nextIndex: number;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % WORKSPACE_TABS.length;
    else if (event.key === "ArrowLeft") nextIndex = (index - 1 + WORKSPACE_TABS.length) % WORKSPACE_TABS.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = WORKSPACE_TABS.length - 1;
    else return;
    event.preventDefault();
    const nextTab = WORKSPACE_TABS[nextIndex];
    if (nextTab) goToTab(nextTab.key);
  }

  const signupUrl = `${window.location.origin}/join/${tenant.slug}`;

  return (
    <div className={styles.workspace}>
      {headerNotice}
      <div
        className={styles.tablist}
        role="tablist"
        aria-orientation="horizontal"
        aria-label={`${tenant.name} workspace sections`}
      >
        {WORKSPACE_TABS.map((tab, index) => {
          const selected = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              id={`${idBase}-tab-${tab.key}`}
              aria-controls={`${idBase}-panel-${tab.key}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              className={selected ? `${styles.tab} ${styles.tabActive}` : styles.tab}
              onClick={() => setActiveTab(tab.key)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id={`${idBase}-panel-overview`}
        aria-labelledby={`${idBase}-tab-overview`}
        hidden={activeTab !== "overview"}
        className={styles.panel}
      >
        <div className={styles.overviewIntro}>
          <h2 className={styles.overviewHeading}>Signup destination</h2>
          <p className={styles.overviewCopy}>
            Share this link or QR code at checkout so shoppers can join {tenant.name} in seconds.
          </p>
        </div>
        <QrPanel tenantName={tenant.name} url={signupUrl} />
        <div className={styles.overviewNav}>
          <Button type="button" variant="secondary" onClick={() => goToTab("analytics")}>
            View analytics
            <IconArrowRight width={15} height={15} />
          </Button>
          <Button type="button" variant="secondary" onClick={() => goToTab("customize")}>
            Customize brand kit
            <IconArrowRight width={15} height={15} />
          </Button>
          <Button type="button" variant="secondary" onClick={() => goToTab("members")}>
            View members
            <IconArrowRight width={15} height={15} />
          </Button>
        </div>
      </div>

      <div
        role="tabpanel"
        id={`${idBase}-panel-analytics`}
        aria-labelledby={`${idBase}-tab-analytics`}
        hidden={activeTab !== "analytics"}
        className={styles.panel}
      >
        <div className={styles.analyticsIntro}>
          <h2 className={styles.analyticsHeading}>Analytics</h2>
          <p className={styles.analyticsCopy}>How {tenant.name} is growing, refreshed each time you open this tab.</p>
        </div>
        <AnalyticsPanel
          tenantName={tenant.name}
          state={analyticsState}
          onRetry={() => setAnalyticsRetry((current) => current + 1)}
        />
      </div>

      <div
        role="tabpanel"
        id={`${idBase}-panel-customize`}
        aria-labelledby={`${idBase}-tab-customize`}
        hidden={activeTab !== "customize"}
        className={styles.panel}
      >
        <BrandSettings
          tenant={tenant}
          token={token}
          canEdit={canEditBrand}
          onUnauthorized={onUnauthorized}
          onTenantUpdated={onTenantUpdated}
        />
      </div>

      <div
        role="tabpanel"
        id={`${idBase}-panel-members`}
        aria-labelledby={`${idBase}-tab-members`}
        hidden={activeTab !== "members"}
        className={styles.panel}
      >
        <div className={styles.membersHeader}>
          <h2 className={styles.membersHeading}>Members</h2>
          <p className={styles.membersCopy}>Everyone who has joined {tenant.name}, most recent first.</p>
        </div>
        {error && <InlineNotice tone="error">{error}</InlineNotice>}
        <MemberTable
          tenantName={tenant.name}
          members={members}
          hasMore={hasMore}
          loadingMore={loadingMore}
          loadingFirstPage={loadingFirstPage}
          onLoadMore={() => void handleLoadMore()}
        />
      </div>
    </div>
  );
}
